import {
	pauseAllMedia,
	PUB_SUB_EVENTS,
	subscribe,
	VariantChangeImmediateEvent,
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import {
	isSliderComponent,
	type SliderComponent,
} from '@/scripts/theme/slider-component'
import { type StickyHeader } from '@/scripts/theme/sticky-header'
import { type DeferredMedia } from '@/scripts/theme/deferred-media'
import { UcoastEl } from '@/scripts/core/UcoastEl'

export class MediaGallery extends UcoastEl {
	static htmlSelector = 'media-gallery'
	elements: {
		liveRegion: HTMLElement
		viewer: SliderComponent
	}
	mql: MediaQueryList
	stickyHeader?: StickyHeader
	productId: string
	customVariantMedia?: Record<string, string[]>
	customVariantMediaSquare?: Record<string, string[]>
	honorVariantOrder: boolean
	currentVariantId?: number
	constructor() {
		super()
		this.elements = {
			liveRegion: q.rs('[id^="GalleryStatus"]', this),
			viewer: q.rs('[id^="GalleryViewer"]', this),
		}
		this.productId = q.ra(this, 'data-product-id')
		this.mql = window.matchMedia('(min-width: 750px)')
		// Desktop uses the portrait (variant_media) order; mobile uses the square
		// (variant_media_square) order. Either map may be absent.
		this.customVariantMedia = this.getCustomVariantMedia('data-variant-media-ids')
		this.customVariantMediaSquare = this.getCustomVariantMedia(
			'data-variant-media-ids-square'
		)
		// Section setting: honor the variant metafield order (default), or fall
		// back to the product's Media-section order. Absent attr = default on.
		this.honorVariantOrder = q.oa(this, 'data-honor-variant-order') !== 'false'

		subscribe(
			PUB_SUB_EVENTS.variantChangeImmediate,
			this.switchToFeaturedMedia.bind(this)
		)
		// Portrait and square metafields can define different orders, so re-apply
		// when crossing the mobile/desktop breakpoint.
		this.mql.addEventListener('change', () => {
			if (this.currentVariantId != null)
				this.switchCustomMedia(this.currentVariantId)
		})
	}

	getCustomVariantMedia(dataAttr: string) {
		const json = q.os<HTMLScriptElement>(
			`script[type="application/json"][${dataAttr}]`,
			this
		)?.textContent
		if (!json) return
		try {
			return JSON.parse(json) as Record<string, string[]>
		} catch (error) {
			console.error(
				'Error parsing JSON, check variant-media-ids-from-metafield.liquid and make sure theres no commas that shouldnt be there',
				error
			)
		}
	}

	// Desktop → portrait (variant_media); mobile → square (variant_media_square).
	// Resolved PER variant: if the preferred set isn't configured for this
	// variant, fall back to the other so it still gets a custom order — e.g. a
	// variant with no square media uses its ordinary variant_media order on
	// mobile instead of dropping to the unfiltered product gallery.
	getActiveCustomMediaForVariant(variantId: number) {
		const key = `${variantId}`
		const portrait = this.customVariantMedia?.[key]
		const square = this.customVariantMediaSquare?.[key]
		const isMobile = !this.mql.matches
		const preferred = isMobile ? square : portrait
		const fallback = isMobile ? portrait : square
		if (preferred && preferred.length > 0) return preferred
		if (fallback && fallback.length > 0) return fallback
		return undefined
	}

	switchToFeaturedMedia(event: VariantChangeImmediateEvent) {
		const variantId = event.data.variantId
		 const usedCustomGallery = this.switchCustomMedia(variantId)
    if (usedCustomGallery) return
		const featuredMedia = q.rs(
			`[data-uc-featured-media][data-variant-id="${variantId}"]`,
			this.elements.viewer
		)
		const featuredMediaId = q.ra(featuredMedia, 'data-gallery-media-id')
		this.setActiveMedia(featuredMediaId, false)
	}

	getActiveMediaParent(mediaId: string) {
		return q.rs(
			`[data-gallery-media-id="${mediaId}"]`,
			this.elements.viewer,
			'parentElement'
		)
	}

	setActiveMedia(mediaId: string, prepend: boolean) {
		const activeMedia = q.rs(
			`[data-gallery-media-id="${mediaId}"]`,
			this.elements.viewer
		)
		q.rl('[data-gallery-media-id]', this.elements.viewer).forEach((element) => {
			element.classList.remove('is-active')
      const elMediaId = q.ra(element, 'data-gallery-media-id')
      if (elMediaId=== mediaId) {
        element.classList.remove('hidden')
        element.classList.add('is-active')
        void window.Ucoast.mediaManager.playAllInContainer(element)
      } else {
        element.classList.add('hidden')
        element.classList.remove('is-active')
        void window.Ucoast.mediaManager.pauseAllInContainer(element)
      }
		})

		if (prepend) {
			const activeMediaParent = this.getActiveMediaParent(mediaId)
			activeMediaParent.prepend(activeMedia)

			if (isSliderComponent(this.elements.viewer))
				this.elements.viewer.resetPages()
		}

		this.preventStickyHeader()
		window.setTimeout(() => {
			if (this.dataset.desktopLayout === 'stacked') {
				activeMedia.scrollIntoView({ behavior: 'smooth' })
			}
		})
		this.playActiveMedia(activeMedia)
	}

	announceLiveRegion(activeItem: HTMLElement, position: string) {
		const image = q.rs<HTMLImageElement>(
			'.product__modal-opener--image img',
			activeItem
		)
		if (!image) return
		image.onload = () => {
			this.elements.liveRegion.setAttribute('aria-hidden', 'false')
			this.elements.liveRegion.innerHTML =
				window.accessibilityStrings.imageAvailable.replace(
					'[index]',
					position
				)
			setTimeout(() => {
				this.elements.liveRegion.setAttribute('aria-hidden', 'true')
			}, 2000)
		}
		image.src = image.src
	}

	playActiveMedia(activeItem: HTMLElement) {
		pauseAllMedia()
		const deferredMedia = q.os<DeferredMedia>('.deferred-media', activeItem)
		if (deferredMedia) deferredMedia.loadContent(false)
	}

	preventStickyHeader() {
		this.stickyHeader =
			this.stickyHeader || q.rs<StickyHeader>('sticky-header')
		if (!this.stickyHeader) return
		this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'))
	}

	removeListSemantic() {
		if (!isSliderComponent(this.elements.viewer)) return
		this.elements.viewer.slider.setAttribute('role', 'presentation')
		this.elements.viewer.sliderItems?.forEach((slide) =>
			slide.setAttribute('role', 'presentation')
		)
	}

	resetCustomMedia() {
		this.classList.remove('uc-square-media')
		q.rl('[data-gallery-media-id]', this.elements.viewer).forEach((li) => {
			li.classList.remove('hidden')
		})
	}

	switchCustomMedia(variantId: number) {
		this.currentVariantId = variantId
		// Setting off → show the ordinary product gallery (product images): no
		// variant filtering or reordering. So a variant without square media on
		// mobile shows product images when off, or its variant_media order when on.
		if (!this.honorVariantOrder) {
			this.resetCustomMedia()
			return false
		}
		const customMedia = this.getActiveCustomMediaForVariant(variantId)
		if (!this.elements.viewer || !customMedia || customMedia.length < 1) {
			this.resetCustomMedia()
			return false
		}
		// Square media (1:1) needs a square frame; the mobile slide is otherwise
		// hard-set to 4:5 and object-cover would crop the square photos.
		const usingSquare =
			customMedia === this.customVariantMediaSquare?.[`${variantId}`]
		this.classList.toggle('uc-square-media', usingSquare)
		const slides = q.rl('[data-gallery-media-id]', this.elements.viewer)
		slides.forEach((li) => {
			const mediaIdStr = q.ra(li, 'data-gallery-media-id').split('-')
			// get last
			const mediaId = mediaIdStr[mediaIdStr.length - 1]
			if (!customMedia.includes(mediaId.trim())) {
				li.classList.add('hidden')
        li.classList.remove('is-active')
        void window.Ucoast.mediaManager.pauseAllInContainer(li)
			} else {
				li.classList.remove('hidden')
        li.classList.add('is-active')
        void window.Ucoast.mediaManager.playAllInContainer(li)
			}
		})
		this.reorderCustomMedia(customMedia, slides)
		window.setTimeout(() => {
			this.elements.viewer.setSlidePosition(0)
		}, 100)
		return true
	}

	// Slides are rendered in the product's media order, but the visible ones
	// must follow the order defined in the variant_media metafield. Re-append
	// the visible slides in that order so the DOM (and therefore the slider)
	// matches the merchant's configured order.
	reorderCustomMedia(customMedia: string[], slides: NodeListOf<HTMLElement>) {
		if (slides.length < 1) return
		const parent = slides[0].parentElement
		if (!parent) return
		// A media id can appear on more than one slide (e.g. a variant's
		// featured media rendered alongside the same product media), so group
		// matches by id and keep them together.
		const slidesByMediaId = new Map<string, HTMLElement[]>()
		slides.forEach((li) => {
			const mediaIdStr = q.ra(li, 'data-gallery-media-id').split('-')
			const mediaId = mediaIdStr[mediaIdStr.length - 1].trim()
			const existing = slidesByMediaId.get(mediaId) ?? []
			existing.push(li)
			slidesByMediaId.set(mediaId, existing)
		})
		// Only the FIRST metafield entry is the active/primary (main) image. The
		// show/hide loop marks every shown slide is-active, but in the thumbnail
		// layout the main image is the is-active one, so leaving the variant's
		// featured slide active keeps it pinned as the "first" photo. Activate
		// only customMedia[0] here so the metafield's first entry wins.
		let firstActiveSet = false
		customMedia.forEach((mediaId, index) => {
			const matches = slidesByMediaId.get(`${mediaId}`.trim())
			if (!matches) return
			matches.forEach((li) => {
				parent.appendChild(li)
				if (index === 0 && !firstActiveSet) {
					li.classList.add('is-active')
					firstActiveSet = true
				} else {
					li.classList.remove('is-active')
				}
			})
		})
		if (isSliderComponent(this.elements.viewer)) {
			this.elements.viewer.resetPages()
		}
	}
}
